import React, { useState, useEffect } from 'react';
import {
  ChatBubbleLeftRightIcon,
  SpeakerWaveIcon,
  VideoCameraIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon,
  NewspaperIcon,
  MicrophoneIcon,
  PresentationChartBarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface CommunicationInteractiveProps {
  content: {
    id: string;
    title: string;
    description: string;
    type: string;
    interactiveData: {
      exerciseType: 'public-speaking' | 'media-analysis' | 'written-communication' | 'interpersonal-skills' | 'digital-storytelling' | 'cultural_analysis' | 'message_analysis';
      instructions: string;
      scenario?: any;
      expectedResults?: any;
      hints?: string[];
    };
  };
  onComplete: (completed: boolean, score?: number) => void;
}

const CommunicationInteractive: React.FC<CommunicationInteractiveProps> = ({
  content,
  onComplete
}) => {
  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [results, setResults] = useState<Record<string, any>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [currentHint, setCurrentHint] = useState(0);
  
  // Tracking states
  const [timeSpent, setTimeSpent] = useState(0);
  const [windowFocused, setWindowFocused] = useState(true);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [inputInteractions, setInputInteractions] = useState(0);
  const [minimumEngagementTime] = useState(180); // 3 minutes for communication planning
  const [isEngaged, setIsEngaged] = useState(false);

  const { exerciseType, instructions, scenario, hints } = content.interactiveData;

  useEffect(() => {
    initializeExercise();
  }, [exerciseType]);

  // Window focus tracking
  useEffect(() => {
    if (isSubmitted) return;

    const handleFocus = () => setWindowFocused(true);
    const handleBlur = () => {
      setWindowFocused(false);
      setTabSwitches(prev => prev + 1);
      if (tabSwitches >= 2) {
        toast('⚠️ Stay focused on your communication work for best results.', {
          icon: '🎤',
          duration: 3000,
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setWindowFocused(false);
      } else {
        setWindowFocused(true);
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSubmitted, tabSwitches]);

  // Time tracking
  useEffect(() => {
    if (isSubmitted) return;

    const interval = setInterval(() => {
      if (windowFocused) {
        setTimeSpent(prev => {
          const newTime = prev + 1;
          if (newTime >= minimumEngagementTime && !isEngaged) {
            setIsEngaged(true);
          }
          return newTime;
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [windowFocused, isSubmitted, minimumEngagementTime, isEngaged]);

  const initializeExercise = () => {
    switch (exerciseType) {
      case 'public-speaking':
        setInputs({
          speechTopic: '',
          audience: '',
          objective: '',
          outline: ['', '', ''],
          openingHook: '',
          mainPoints: ['', '', ''],
          closingStatement: '',
          deliveryNotes: ''
        });
        break;
      case 'media-analysis':
        setInputs({
          mediaType: '',
          source: '',
          bias: '',
          audience: '',
          techniques: ['', ''],
          credibility: '',
          impact: '',
          critique: ''
        });
        break;
      case 'written-communication':
        setInputs({
          documentType: '',
          purpose: '',
          audience: '',
          tone: '',
          structure: ['', '', ''],
          keyMessages: ['', ''],
          callToAction: '',
          revision: ''
        });
        break;
      case 'interpersonal-skills':
        setInputs({
          scenario: '',
          communicationGoals: ['', ''],
          activeListening: '',
          empathy: '',
          conflictResolution: '',
          nonverbalCues: '',
          feedback: ''
        });
        break;
      case 'digital-storytelling':
        setInputs({
          storyTopic: '',
          targetAudience: '',
          narrative: '',
          visualElements: ['', '', ''],
          audioElements: '',
          platform: '',
          engagement: '',
          callToAction: ''
        });
        break;
      case 'cultural_analysis':
        setInputs({
          selectedCultures: [],
          communicationContext: '',
          contextLevel: '',
          powerDistance: '',
          uncertaintyAvoidance: '',
          culturalFactors: ['', '', ''],
          adaptations: ['', '', ''],
          effectiveness: '',
          recommendations: ''
        });
        break;
      case 'message_analysis':
        setInputs({
          selectedChannels: [],
          brandMessage: '',
          coreMessage: '',
          keyValues: ['', '', ''],
          channelMessages: {
            television: '',
            digital: '',
            print: '',
            social_media: '',
            out_of_home: ''
          },
          consistencyRating: '',
          inconsistencies: ['', ''],
          improvements: ['', ''],
          overallAssessment: ''
        });
        break;
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setInputInteractions(prev => prev + 1);
  };

  const validateSubmission = () => {
    if (!isEngaged) {
      toast.error(`Please spend at least ${Math.ceil(minimumEngagementTime / 60)} minutes on your communication work.`);
      return false;
    }

    const requiredFields = getRequiredFields();
    const missingFields = requiredFields.filter(field => {
      const value = inputs[field];
      return !value || (Array.isArray(value) ? value.every(v => !v) : !value.length);
    });
    
    if (missingFields.length > 0) {
      toast.error('Please complete all required fields before submitting.');
      return false;
    }

    return true;
  };

  const getRequiredFields = () => {
    switch (exerciseType) {
      case 'public-speaking':
        return ['speechTopic', 'objective', 'openingHook'];
      case 'media-analysis':
        return ['mediaType', 'bias', 'credibility'];
      case 'written-communication':
        return ['documentType', 'purpose', 'audience'];
      case 'interpersonal-skills':
        return ['activeListening', 'empathy'];
      case 'digital-storytelling':
        return ['storyTopic', 'narrative', 'platform'];
      case 'cultural_analysis':
        return ['communicationContext', 'contextLevel', 'effectiveness'];
      case 'message_analysis':
        return ['brandMessage', 'coreMessage', 'consistencyRating'];
      default:
        return [];
    }
  };

  const evaluateSubmission = () => {
    switch (exerciseType) {
      case 'public-speaking':
        return evaluatePublicSpeaking();
      case 'media-analysis':
        return evaluateMediaAnalysis();
      case 'written-communication':
        return evaluateWrittenCommunication();
      case 'interpersonal-skills':
        return evaluateInterpersonalSkills();
      case 'digital-storytelling':
        return evaluateDigitalStorytelling();
      case 'cultural_analysis':
        return evaluateCulturalAnalysis();
      case 'message_analysis':
        return evaluateMessageAnalysis();
      default:
        return { score: 0, feedback: 'Unknown exercise type' };
    }
  };

  const evaluatePublicSpeaking = () => {
    let score = 0;
    const feedback = [];

    // Speech topic and objective (20 points)
    if (inputs.speechTopic && inputs.objective?.length > 30) {
      score += 20;
      feedback.push('✓ Clear speech topic and objective');
    }

    // Opening hook (15 points)
    if (inputs.openingHook?.length > 30) {
      score += 15;
      feedback.push('✓ Engaging opening hook provided');
    } else {
      feedback.push('Strengthen your opening to capture audience attention');
    }

    // Main points structure (30 points)
    const validPoints = inputs.mainPoints?.filter((point: string) => point.length > 25).length || 0;
    score += Math.min(30, validPoints * 10);
    if (validPoints >= 2) {
      feedback.push('✓ Well-structured main points');
    }

    // Audience consideration (20 points)
    if (inputs.audience?.length > 20) {
      score += 20;
      feedback.push('✓ Audience analysis included');
    }

    // Closing statement (15 points)
    if (inputs.closingStatement?.length > 25) {
      score += 15;
      feedback.push('✓ Strong closing statement');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateMediaAnalysis = () => {
    let score = 0;
    const feedback = [];

    // Media type identification (15 points)
    if (inputs.mediaType) {
      score += 15;
      feedback.push('✓ Media type identified');
    }

    // Bias analysis (30 points)
    if (inputs.bias?.length > 60) {
      score += 30;
      feedback.push('✓ Comprehensive bias analysis');
    } else {
      feedback.push('Expand your bias analysis with specific examples');
    }

    // Credibility assessment (25 points)
    if (inputs.credibility?.length > 50) {
      score += 25;
      feedback.push('✓ Credibility thoroughly assessed');
    }

    // Communication techniques (20 points)
    const validTechniques = inputs.techniques?.filter((tech: string) => tech.length > 15).length || 0;
    score += Math.min(20, validTechniques * 10);

    // Impact and critique (10 points)
    if (inputs.critique?.length > 40) {
      score += 10;
      feedback.push('✓ Critical analysis provided');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateWrittenCommunication = () => {
    let score = 0;
    const feedback = [];

    // Document purpose and audience (25 points)
    if (inputs.purpose?.length > 30 && inputs.audience?.length > 20) {
      score += 25;
      feedback.push('✓ Clear purpose and audience identification');
    }

    // Tone appropriateness (20 points)
    if (inputs.tone?.length > 15) {
      score += 20;
      feedback.push('✓ Appropriate tone selected');
    }

    // Document structure (30 points)
    const validStructure = inputs.structure?.filter((s: string) => s.length > 20).length || 0;
    score += Math.min(30, validStructure * 10);

    // Key messages (15 points)
    const validMessages = inputs.keyMessages?.filter((msg: string) => msg.length > 20).length || 0;
    score += Math.min(15, validMessages * 7.5);

    // Call to action (10 points)
    if (inputs.callToAction?.length > 20) {
      score += 10;
      feedback.push('✓ Clear call to action');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateInterpersonalSkills = () => {
    let score = 0;
    const feedback = [];

    // Active listening (30 points)
    if (inputs.activeListening?.length > 50) {
      score += 30;
      feedback.push('✓ Active listening strategies demonstrated');
    } else {
      feedback.push('Expand on active listening techniques');
    }

    // Empathy demonstration (25 points)
    if (inputs.empathy?.length > 40) {
      score += 25;
      feedback.push('✓ Empathy and understanding shown');
    }

    // Conflict resolution (25 points)
    if (inputs.conflictResolution?.length > 40) {
      score += 25;
      feedback.push('✓ Conflict resolution approach outlined');
    }

    // Nonverbal communication (10 points)
    if (inputs.nonverbalCues?.length > 30) {
      score += 10;
      feedback.push('✓ Nonverbal communication considered');
    }

    // Communication goals (10 points)
    const validGoals = inputs.communicationGoals?.filter((goal: string) => goal.length > 15).length || 0;
    score += Math.min(10, validGoals * 5);

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateDigitalStorytelling = () => {
    let score = 0;
    const feedback = [];

    // Story topic and narrative (25 points)
    if (inputs.storyTopic && inputs.narrative?.length > 80) {
      score += 25;
      feedback.push('✓ Compelling story and narrative');
    }

    // Target audience (15 points)
    if (inputs.targetAudience?.length > 20) {
      score += 15;
      feedback.push('✓ Target audience defined');
    }

    // Visual elements (25 points)
    const validVisuals = inputs.visualElements?.filter((visual: string) => visual.length > 15).length || 0;
    score += Math.min(25, validVisuals * 8.33);

    // Platform selection (15 points)
    if (inputs.platform?.length > 10) {
      score += 15;
      feedback.push('✓ Appropriate platform selected');
    }

    // Engagement strategy (20 points)
    if (inputs.engagement?.length > 30) {
      score += 20;
      feedback.push('✓ Engagement strategy included');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateCulturalAnalysis = () => {
    let score = 0;
    const feedback = [];

    // Communication context analysis (20 points)
    if (inputs.communicationContext?.length > 50) {
      score += 20;
      feedback.push('✓ Thorough communication context analysis');
    }

    // Cultural dimension understanding (25 points)
    if (inputs.contextLevel && inputs.powerDistance && inputs.uncertaintyAvoidance) {
      score += 25;
      feedback.push('✓ Good understanding of cultural dimensions');
    }

    // Cultural factors identified (20 points)
    const validFactors = inputs.culturalFactors?.filter((f: string) => f && f.length > 20).length || 0;
    if (validFactors >= 2) {
      score += 20;
      feedback.push('✓ Key cultural factors well-identified');
    }

    // Adaptation strategies (20 points)
    const validAdaptations = inputs.adaptations?.filter((a: string) => a && a.length > 30).length || 0;
    if (validAdaptations >= 2) {
      score += 20;
      feedback.push('✓ Practical adaptation strategies provided');
    }

    // Effectiveness assessment and recommendations (15 points)
    if (inputs.effectiveness?.length > 40 && inputs.recommendations?.length > 60) {
      score += 15;
      feedback.push('✓ Comprehensive effectiveness assessment');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateMessageAnalysis = () => {
    let score = 0;
    const feedback = [];

    // Brand message definition (20 points)
    if (inputs.brandMessage?.length > 50 && inputs.coreMessage?.length > 40) {
      score += 20;
      feedback.push('✓ Clear brand and core message definition');
    }

    // Key values identification (15 points)
    const validValues = inputs.keyValues?.filter((v: string) => v && v.length > 15).length || 0;
    if (validValues >= 2) {
      score += 15;
      feedback.push('✓ Key brand values well-identified');
    }

    // Channel message analysis (25 points)
    const channelCount = Object.values(inputs.channelMessages || {}).filter((msg: any) => msg && msg.length > 30).length;
    if (channelCount >= 3) {
      score += 25;
      feedback.push('✓ Comprehensive channel message analysis');
    }

    // Consistency rating and assessment (20 points)
    if (inputs.consistencyRating && inputs.overallAssessment?.length > 60) {
      score += 20;
      feedback.push('✓ Thorough consistency evaluation');
    }

    // Inconsistencies and improvements (20 points)
    const validInconsistencies = inputs.inconsistencies?.filter((i: string) => i && i.length > 20).length || 0;
    const validImprovements = inputs.improvements?.filter((i: string) => i && i.length > 25).length || 0;
    if (validInconsistencies >= 1 && validImprovements >= 1) {
      score += 20;
      feedback.push('✓ Detailed analysis of inconsistencies and improvement strategies');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const handleSubmit = () => {
    if (!validateSubmission()) return;

    const evaluation = evaluateSubmission();
    const finalScore = Math.max(0, Math.min(100, evaluation.score));
    const passed = finalScore >= 70;

    setResults({
      score: finalScore,
      feedback: evaluation.feedback,
      passed,
      timeSpent,
      interactions: inputInteractions
    });

    setIsSubmitted(true);
    setIsCorrect(passed);
    
    onComplete(passed, finalScore);

    if (passed) {
      toast.success(`Excellent communication work! Score: ${finalScore}%`);
    } else {
      toast.error(`Score: ${finalScore}%. Review the feedback and try again.`);
    }
  };

  const getExerciseIcon = () => {
    switch (exerciseType) {
      case 'public-speaking': return <MicrophoneIcon className="h-6 w-6" />;
      case 'media-analysis': return <NewspaperIcon className="h-6 w-6" />;
      case 'written-communication': return <ChatBubbleLeftRightIcon className="h-6 w-6" />;
      case 'interpersonal-skills': return <SpeakerWaveIcon className="h-6 w-6" />;
      case 'digital-storytelling': return <VideoCameraIcon className="h-6 w-6" />;
      case 'cultural_analysis': return <ChatBubbleLeftRightIcon className="h-6 w-6" />;
      case 'message_analysis': return <NewspaperIcon className="h-6 w-6" />;
      default: return <ChatBubbleLeftRightIcon className="h-6 w-6" />;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderExerciseContent = () => {
    switch (exerciseType) {
      case 'public-speaking':
        return (
          <div className="space-y-6">
            {scenario?.speechContext && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Speaking Context:</h4>
                <div className="text-sm text-gray-700">{scenario.speechContext}</div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Speech Topic *
                </label>
                <input
                  type="text"
                  value={inputs.speechTopic || ''}
                  onChange={(e) => handleInputChange('speechTopic', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your speech topic"
                  disabled={isSubmitted}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience
                </label>
                <input
                  type="text"
                  value={inputs.audience || ''}
                  onChange={(e) => handleInputChange('audience', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe your audience"
                  disabled={isSubmitted}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Speech Objective *
              </label>
              <textarea
                value={inputs.objective || ''}
                onChange={(e) => handleInputChange('objective', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="What do you want to achieve with this speech?"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Opening Hook *
              </label>
              <textarea
                value={inputs.openingHook || ''}
                onChange={(e) => handleInputChange('openingHook', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="How will you capture your audience's attention from the start?"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Main Points
              </label>
              {[0, 1, 2].map((index) => (
                <textarea
                  key={index}
                  value={inputs.mainPoints?.[index] || ''}
                  onChange={(e) => {
                    const newPoints = [...(inputs.mainPoints || ['', '', ''])];
                    newPoints[index] = e.target.value;
                    handleInputChange('mainPoints', newPoints);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
                  rows={2}
                  placeholder={`Main Point ${index + 1}: Key message with supporting evidence`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Closing Statement
              </label>
              <textarea
                value={inputs.closingStatement || ''}
                onChange={(e) => handleInputChange('closingStatement', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="How will you conclude and call your audience to action?"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delivery Notes
              </label>
              <textarea
                value={inputs.deliveryNotes || ''}
                onChange={(e) => handleInputChange('deliveryNotes', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Notes on pace, gestures, vocal variety, visual aids, etc."
                disabled={isSubmitted}
              />
            </div>
          </div>
        );

      case 'media-analysis':
        return (
          <div className="space-y-6">
            {scenario?.mediaContent && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Media to Analyze:</h4>
                <div className="text-sm text-gray-700">{scenario.mediaContent}</div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Media Type *
                </label>
                <select
                  value={inputs.mediaType || ''}
                  onChange={(e) => handleInputChange('mediaType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  disabled={isSubmitted}
                >
                  <option value="">Select media type...</option>
                  <option value="news-article">News Article</option>
                  <option value="advertisement">Advertisement</option>
                  <option value="social-media">Social Media Post</option>
                  <option value="video">Video Content</option>
                  <option value="podcast">Podcast</option>
                  <option value="infographic">Infographic</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Source
                </label>
                <input
                  type="text"
                  value={inputs.source || ''}
                  onChange={(e) => handleInputChange('source', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Media source or publisher"
                  disabled={isSubmitted}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bias Analysis *
              </label>
              <textarea
                value={inputs.bias || ''}
                onChange={(e) => handleInputChange('bias', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Identify any bias in language, presentation, or perspective. Provide specific examples."
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Audience
              </label>
              <textarea
                value={inputs.audience || ''}
                onChange={(e) => handleInputChange('audience', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Who is the intended audience for this media?"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Communication Techniques
              </label>
              {[0, 1].map((index) => (
                <input
                  key={index}
                  type="text"
                  value={inputs.techniques?.[index] || ''}
                  onChange={(e) => {
                    const newTechniques = [...(inputs.techniques || ['', ''])];
                    newTechniques[index] = e.target.value;
                    handleInputChange('techniques', newTechniques);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
                  placeholder={`Technique ${index + 1}: e.g., emotional appeals, statistics, expert testimony`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Credibility Assessment *
              </label>
              <textarea
                value={inputs.credibility || ''}
                onChange={(e) => handleInputChange('credibility', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Evaluate the credibility of sources, evidence, and claims presented."
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Impact and Critique
              </label>
              <textarea
                value={inputs.critique || ''}
                onChange={(e) => handleInputChange('critique', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="What impact might this media have on its audience? Your overall critique?"
                disabled={isSubmitted}
              />
            </div>
          </div>
        );

      case 'written-communication':
        return (
          <div className="space-y-6">
            {scenario?.writingContext && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Writing Context:</h4>
                <div className="text-sm text-gray-700">{scenario.writingContext}</div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Type *
                </label>
                <select
                  value={inputs.documentType || ''}
                  onChange={(e) => handleInputChange('documentType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  disabled={isSubmitted}
                >
                  <option value="">Select document type...</option>
                  <option value="business-letter">Business Letter</option>
                  <option value="email">Professional Email</option>
                  <option value="report">Report</option>
                  <option value="proposal">Proposal</option>
                  <option value="memo">Memorandum</option>
                  <option value="press-release">Press Release</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tone
                </label>
                <select
                  value={inputs.tone || ''}
                  onChange={(e) => handleInputChange('tone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  disabled={isSubmitted}
                >
                  <option value="">Select tone...</option>
                  <option value="formal">Formal</option>
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="persuasive">Persuasive</option>
                  <option value="informative">Informative</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Purpose *
              </label>
              <textarea
                value={inputs.purpose || ''}
                onChange={(e) => handleInputChange('purpose', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="What is the main purpose of this document?"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Audience *
              </label>
              <textarea
                value={inputs.audience || ''}
                onChange={(e) => handleInputChange('audience', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Describe your target audience and their needs"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Structure
              </label>
              {[0, 1, 2].map((index) => (
                <textarea
                  key={index}
                  value={inputs.structure?.[index] || ''}
                  onChange={(e) => {
                    const newStructure = [...(inputs.structure || ['', '', ''])];
                    newStructure[index] = e.target.value;
                    handleInputChange('structure', newStructure);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
                  rows={2}
                  placeholder={`Section ${index + 1}: ${index === 0 ? 'Introduction/Opening' : index === 1 ? 'Body/Main Content' : 'Conclusion/Closing'}`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Key Messages
              </label>
              {[0, 1].map((index) => (
                <input
                  key={index}
                  type="text"
                  value={inputs.keyMessages?.[index] || ''}
                  onChange={(e) => {
                    const newMessages = [...(inputs.keyMessages || ['', ''])];
                    newMessages[index] = e.target.value;
                    handleInputChange('keyMessages', newMessages);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
                  placeholder={`Key Message ${index + 1}: Main points you want to communicate`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Call to Action
              </label>
              <textarea
                value={inputs.callToAction || ''}
                onChange={(e) => handleInputChange('callToAction', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="What action do you want your audience to take after reading?"
                disabled={isSubmitted}
              />
            </div>
          </div>
        );

      case 'interpersonal-skills':
        return (
          <div className="space-y-6">
            {scenario?.interpersonalScenario && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Communication Scenario:</h4>
                <div className="text-sm text-gray-700">{scenario.interpersonalScenario}</div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scenario Analysis
              </label>
              <textarea
                value={inputs.scenario || ''}
                onChange={(e) => handleInputChange('scenario', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Describe the communication challenges in this scenario"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Communication Goals
              </label>
              {[0, 1].map((index) => (
                <input
                  key={index}
                  type="text"
                  value={inputs.communicationGoals?.[index] || ''}
                  onChange={(e) => {
                    const newGoals = [...(inputs.communicationGoals || ['', ''])];
                    newGoals[index] = e.target.value;
                    handleInputChange('communicationGoals', newGoals);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
                  placeholder={`Goal ${index + 1}: What do you want to achieve in this conversation?`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Active Listening Strategy *
              </label>
              <textarea
                value={inputs.activeListening || ''}
                onChange={(e) => handleInputChange('activeListening', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="How will you demonstrate active listening? Include techniques like paraphrasing, asking clarifying questions, etc."
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Empathy and Understanding *
              </label>
              <textarea
                value={inputs.empathy || ''}
                onChange={(e) => handleInputChange('empathy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="How will you show empathy and understanding of the other person's perspective?"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conflict Resolution Approach
              </label>
              <textarea
                value={inputs.conflictResolution || ''}
                onChange={(e) => handleInputChange('conflictResolution', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="If there's conflict, how will you work toward resolution? Include de-escalation techniques."
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nonverbal Communication
              </label>
              <textarea
                value={inputs.nonverbalCues || ''}
                onChange={(e) => handleInputChange('nonverbalCues', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Consider body language, tone of voice, facial expressions, etc."
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Feedback Approach
              </label>
              <textarea
                value={inputs.feedback || ''}
                onChange={(e) => handleInputChange('feedback', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="If you need to give feedback, how will you do it constructively?"
                disabled={isSubmitted}
              />
            </div>
          </div>
        );

      case 'digital-storytelling':
        return (
          <div className="space-y-6">
            {scenario?.storyContext && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Story Context:</h4>
                <div className="text-sm text-gray-700">{scenario.storyContext}</div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Story Topic *
                </label>
                <input
                  type="text"
                  value={inputs.storyTopic || ''}
                  onChange={(e) => handleInputChange('storyTopic', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="What is your story about?"
                  disabled={isSubmitted}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Platform *
                </label>
                <select
                  value={inputs.platform || ''}
                  onChange={(e) => handleInputChange('platform', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  disabled={isSubmitted}
                >
                  <option value="">Select platform...</option>
                  <option value="youtube">YouTube</option>
                  <option value="instagram">Instagram</option>
                  <option value="tiktok">TikTok</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="website">Website/Blog</option>
                  <option value="podcast">Podcast</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Audience
              </label>
              <textarea
                value={inputs.targetAudience || ''}
                onChange={(e) => handleInputChange('targetAudience', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Who is your target audience for this digital story?"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Story Narrative *
              </label>
              <textarea
                value={inputs.narrative || ''}
                onChange={(e) => handleInputChange('narrative', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Tell your story with a clear beginning, middle, and end. Include emotional elements and key messages."
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Visual Elements
              </label>
              {[0, 1, 2].map((index) => (
                <input
                  key={index}
                  type="text"
                  value={inputs.visualElements?.[index] || ''}
                  onChange={(e) => {
                    const newVisuals = [...(inputs.visualElements || ['', '', ''])];
                    newVisuals[index] = e.target.value;
                    handleInputChange('visualElements', newVisuals);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
                  placeholder={`Visual Element ${index + 1}: photos, videos, graphics, animations, etc.`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Audio Elements
              </label>
              <textarea
                value={inputs.audioElements || ''}
                onChange={(e) => handleInputChange('audioElements', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Voiceover, music, sound effects, interview clips, etc."
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Engagement Strategy
              </label>
              <textarea
                value={inputs.engagement || ''}
                onChange={(e) => handleInputChange('engagement', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="How will you engage your audience? Interactive elements, questions, etc."
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Call to Action
              </label>
              <textarea
                value={inputs.callToAction || ''}
                onChange={(e) => handleInputChange('callToAction', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="What do you want viewers to do after experiencing your story?"
                disabled={isSubmitted}
              />
            </div>
          </div>
        );

      case 'cultural_analysis':
        return (
          <div className="space-y-6">
            {scenario && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Scenario: {scenario.scenario}</h4>
                <p className="text-blue-700 text-sm">
                  Analyze communication effectiveness across different cultural contexts: {scenario.cultures?.join(', ')}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Communication Context
              </label>
              <textarea
                value={inputs.communicationContext || ''}
                onChange={(e) => handleInputChange('communicationContext', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Describe the communication context and cultural challenges..."
                disabled={isSubmitted}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Context Level
                </label>
                <select
                  value={inputs.contextLevel || ''}
                  onChange={(e) => handleInputChange('contextLevel', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  disabled={isSubmitted}
                >
                  <option value="">Select...</option>
                  <option value="high-context">High Context</option>
                  <option value="medium-context">Medium Context</option>
                  <option value="low-context">Low Context</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Power Distance
                </label>
                <select
                  value={inputs.powerDistance || ''}
                  onChange={(e) => handleInputChange('powerDistance', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  disabled={isSubmitted}
                >
                  <option value="">Select...</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Uncertainty Avoidance
                </label>
                <select
                  value={inputs.uncertaintyAvoidance || ''}
                  onChange={(e) => handleInputChange('uncertaintyAvoidance', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  disabled={isSubmitted}
                >
                  <option value="">Select...</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cultural Factors (Identify 3 key factors)
              </label>
              {[0, 1, 2].map((index) => (
                <div key={index} className="mb-3">
                  <textarea
                    value={inputs.culturalFactors?.[index] || ''}
                    onChange={(e) => {
                      const newFactors = [...(inputs.culturalFactors || ['', '', ''])];
                      newFactors[index] = e.target.value;
                      handleInputChange('culturalFactors', newFactors);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder={`Cultural factor ${index + 1}...`}
                    disabled={isSubmitted}
                  />
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adaptation Strategies (Identify 3 strategies)
              </label>
              {[0, 1, 2].map((index) => (
                <div key={index} className="mb-3">
                  <textarea
                    value={inputs.adaptations?.[index] || ''}
                    onChange={(e) => {
                      const newAdaptations = [...(inputs.adaptations || ['', '', ''])];
                      newAdaptations[index] = e.target.value;
                      handleInputChange('adaptations', newAdaptations);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder={`Adaptation strategy ${index + 1}...`}
                    disabled={isSubmitted}
                  />
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Effectiveness Assessment
              </label>
              <textarea
                value={inputs.effectiveness || ''}
                onChange={(e) => handleInputChange('effectiveness', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Assess the effectiveness of different communication approaches..."
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recommendations
              </label>
              <textarea
                value={inputs.recommendations || ''}
                onChange={(e) => handleInputChange('recommendations', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Provide specific recommendations for improving cross-cultural communication..."
                disabled={isSubmitted}
              />
            </div>
          </div>
        );

      case 'message_analysis':
        return (
          <div className="space-y-6">
            {scenario && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Scenario: {scenario.scenario}</h4>
                <p className="text-blue-700 text-sm">
                  Analyze message consistency across channels: {scenario.channels?.join(', ')}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand Message
                </label>
                <textarea
                  value={inputs.brandMessage || ''}
                  onChange={(e) => handleInputChange('brandMessage', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Define the overall brand message..."
                  disabled={isSubmitted}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Core Message
                </label>
                <textarea
                  value={inputs.coreMessage || ''}
                  onChange={(e) => handleInputChange('coreMessage', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="What's the core message across all channels?"
                  disabled={isSubmitted}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Key Brand Values (Identify 3)
              </label>
              {[0, 1, 2].map((index) => (
                <div key={index} className="mb-3">
                  <input
                    type="text"
                    value={inputs.keyValues?.[index] || ''}
                    onChange={(e) => {
                      const newValues = [...(inputs.keyValues || ['', '', ''])];
                      newValues[index] = e.target.value;
                      handleInputChange('keyValues', newValues);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    placeholder={`Brand value ${index + 1}...`}
                    disabled={isSubmitted}
                  />
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Channel-Specific Messages
              </label>
              <div className="space-y-4">
                {['television', 'digital', 'print', 'social_media', 'out_of_home'].map((channel) => (
                  <div key={channel}>
                    <label className="block text-sm font-medium text-gray-600 mb-1 capitalize">
                      {channel.replace('_', ' ')} Message
                    </label>
                    <textarea
                      value={inputs.channelMessages?.[channel] || ''}
                      onChange={(e) => {
                        const newMessages = { ...(inputs.channelMessages || {}) };
                        newMessages[channel] = e.target.value;
                        handleInputChange('channelMessages', newMessages);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      rows={2}
                      placeholder={`How is the message adapted for ${channel.replace('_', ' ')}?`}
                      disabled={isSubmitted}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Consistency Rating
              </label>
              <select
                value={inputs.consistencyRating || ''}
                onChange={(e) => handleInputChange('consistencyRating', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                disabled={isSubmitted}
              >
                <option value="">Select consistency rating...</option>
                <option value="excellent">Excellent (90-100%)</option>
                <option value="good">Good (70-89%)</option>
                <option value="fair">Fair (50-69%)</option>
                <option value="poor">Poor (Below 50%)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Identified Inconsistencies
              </label>
              {[0, 1].map((index) => (
                <div key={index} className="mb-3">
                  <textarea
                    value={inputs.inconsistencies?.[index] || ''}
                    onChange={(e) => {
                      const newInconsistencies = [...(inputs.inconsistencies || ['', ''])];
                      newInconsistencies[index] = e.target.value;
                      handleInputChange('inconsistencies', newInconsistencies);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder={`Inconsistency ${index + 1}...`}
                    disabled={isSubmitted}
                  />
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Improvement Recommendations
              </label>
              {[0, 1].map((index) => (
                <div key={index} className="mb-3">
                  <textarea
                    value={inputs.improvements?.[index] || ''}
                    onChange={(e) => {
                      const newImprovements = [...(inputs.improvements || ['', ''])];
                      newImprovements[index] = e.target.value;
                      handleInputChange('improvements', newImprovements);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder={`Improvement recommendation ${index + 1}...`}
                    disabled={isSubmitted}
                  />
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overall Assessment
              </label>
              <textarea
                value={inputs.overallAssessment || ''}
                onChange={(e) => handleInputChange('overallAssessment', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Provide an overall assessment of message consistency across all channels..."
                disabled={isSubmitted}
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <div className="mb-4">
              {getExerciseIcon()}
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              Communication Exercise
            </h4>
            <p className="text-gray-600 mb-6">
              This communication exercise is being developed with specialized communication tools.
            </p>
            
            <button
              onClick={() => onComplete(true, 85)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Complete Exercise (Temporary)
            </button>
          </div>
        );
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getExerciseIcon()}
            <div className="ml-3">
              <h3 className="text-xl font-bold">{content.title}</h3>
              <p className="text-blue-100 text-sm">{content.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
              windowFocused ? 'bg-green-500 bg-opacity-20 text-green-100' : 'bg-red-500 bg-opacity-20 text-red-100'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-1 ${
                windowFocused ? 'bg-green-300' : 'bg-red-300'
              }`}></div>
              {windowFocused ? 'Focused' : 'Not Focused'}
            </div>
            
            <div className="text-sm">
              Time: {formatTime(timeSpent)}
            </div>
            
            {isEngaged && (
              <div className="flex items-center text-xs px-2 py-1 rounded-full bg-green-500 bg-opacity-20 text-green-100">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                Engaged
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <p className="text-gray-700">{instructions}</p>
        
        {!isEngaged && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
              <span>Minimum communication planning time</span>
              <span>{formatTime(timeSpent)} / {formatTime(minimumEngagementTime)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((timeSpent / minimumEngagementTime) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Exercise Content */}
      <div className="p-6">
        {renderExerciseContent()}

        {hints && hints.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setShowHints(!showHints)}
              className="flex items-center text-blue-600 hover:text-blue-700 mb-3"
            >
              <LightBulbIcon className="h-4 w-4 mr-2" />
              {showHints ? 'Hide Hints' : 'Show Hints'}
            </button>
            
            {showHints && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-yellow-800">
                    Hint {currentHint + 1} of {hints.length}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentHint(Math.max(0, currentHint - 1))}
                      disabled={currentHint === 0}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentHint(Math.min(hints.length - 1, currentHint + 1))}
                      disabled={currentHint === hints.length - 1}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
                <p className="text-yellow-700 text-sm">{hints[currentHint]}</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-6">
          {!isSubmitted ? (
            <button
              onClick={handleSubmit}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              {exerciseType === 'public-speaking' ? 'Submit Speech Plan' : 
               exerciseType === 'media-analysis' ? 'Submit Media Analysis' :
               exerciseType === 'written-communication' ? 'Submit Communication Plan' :
               exerciseType === 'interpersonal-skills' ? 'Submit Skills Assessment' :
               'Submit Digital Story'}
            </button>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                {isCorrect ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-red-600 mr-2" />
                )}
                <span className="font-medium">
                  Score: {results.score}% {isCorrect ? '(Passed)' : '(Review Required)'}
                </span>
              </div>
              <div className="text-sm text-gray-600 whitespace-pre-line">
                {results.feedback}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Time spent: {formatTime(results.timeSpent)} | Interactions: {results.interactions}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunicationInteractive;